import { CheckCircle2, AlertTriangle, ArrowRight, Compass } from "lucide-react";

type Status = "aligned" | "partial" | "contradiction" | "missing";

const STATUS_META: Record<Status, { label: string; cls: string; Icon: typeof CheckCircle2 }> = {
  aligned: { label: "Aligned", cls: "bg-primary/10 text-primary", Icon: CheckCircle2 },
  partial: { label: "Partial", cls: "bg-amber-500/10 text-amber-500", Icon: AlertTriangle },
  contradiction: { label: "Contradiction", cls: "bg-destructive/10 text-destructive", Icon: AlertTriangle },
  missing: { label: "Not built", cls: "bg-muted text-muted-foreground", Icon: Compass },
};

const ALIGNMENT: { area: string; status: Status; today: string; roadmap: string }[] = [
  {
    area: "Geographic scope",
    status: "contradiction",
    today: "Alabama-only (Mobile County pilot, 67-county data ceiling). All stats, bills, partners restricted to AL.",
    roadmap: "National / multi-community vision — \"different communities pursue similar goals\" with shared intelligence.",
  },
  {
    area: "Core primitive",
    status: "partial",
    today: "Ideas Board + Projects (promote idea → project, with Coach + grant matching).",
    roadmap: "\"Builds\" — structured, versioned objects with 8 layers (Problem / Asset / Stakeholder / Resource / Action / Impact / Insight / Revision).",
  },
  {
    area: "Audience language",
    status: "contradiction",
    today: "\"Shareholders\" = Advocates, Agencies, Policymakers, Funders. DBE / minority-led small-business focus.",
    roadmap: "Communities, nonprofits, cities, foundations, universities, entrepreneurs, students.",
  },
  {
    area: "Focus areas",
    status: "partial",
    today: "5 pillars (Health & Wellness pink, others sage).",
    roadmap: "8 areas — adds Neighborhood Revitalization, Housing, Food Systems explicitly.",
  },
  {
    area: "Revenue model",
    status: "missing",
    today: "Pricing page exists; no live tiered memberships, no productized services storefront.",
    roadmap: "Free / Individual ($15–30/mo) / Org ($99–499/mo) + Services (grant writing, site assessments, build design) as early revenue engine.",
  },
  {
    area: "Version history / Revision Layer",
    status: "missing",
    today: "Ideas and Projects are mutable; no version/diff history surfaced.",
    roadmap: "Every Build tracks revisions over time — \"living system of knowledge.\"",
  },
  {
    area: "Structured insights",
    status: "partial",
    today: "Insights Hub (editorial articles) + idea comments. Free-form.",
    roadmap: "Structured Insight Layer — reusable, validatable patterns (e.g. \"workforce dev should precede business recruitment\").",
  },
  {
    area: "AI assistance",
    status: "aligned",
    today: "Coach (project + ideas + assessments), grant matching, recommendations. Per-user daily caps live.",
    roadmap: "Phase 4 deliverable — we're ahead of the timeline here.",
  },
  {
    area: "Knowledge platform (Phase 1)",
    status: "aligned",
    today: "Insights, Legislation Tracker, Grant Finder, Community News, Data Explorer, Partner Network.",
    roadmap: "Phase 1 deliverables — case studies, funding DB, research outputs, early services.",
  },
  {
    area: "Services as revenue engine",
    status: "missing",
    today: "No services intake, no client engagement tracking, no \"work → structured framework → Build\" pipeline.",
    roadmap: "Layer 3: Funding Acceleration, Development, Intelligence, Build Design services. Feeds the data flywheel.",
  },
  {
    area: "Stakeholder mapping tools",
    status: "missing",
    today: "Partner Network is a directory; no per-project stakeholder graph.",
    roadmap: "Stakeholder Layer on every Build + Phase 4 stakeholder management tools.",
  },
  {
    area: "Impact tracking",
    status: "partial",
    today: "Projects have rubric/score; no longitudinal outcome tracking (jobs, occupancy, foot traffic, etc.).",
    roadmap: "Impact Layer with measurable indicators per Build; Phase 4 dashboards.",
  },
];

const PIVOTS: { title: string; body: string; phase: string }[] = [
  {
    phase: "Now (next 30–60 days)",
    title: "Reconcile AL-only positioning with national vision",
    body:
      "Decide explicitly: AL stays the proving ground (current copy + memory) or we open the lens. If AL stays, add \"Alabama pilot of a national system\" framing on About / Hero so the roadmap story holds. Update mem://project memory accordingly.",
  },
  {
    phase: "Now",
    title: "Map current objects to \"Build\" vocabulary",
    body:
      "Add an internal mapping doc: Idea = early Build, Project = active Build. Either rename UI to Build or commit to current names and update the roadmap doc. Pick one — drift here will confuse contributors.",
  },
  {
    phase: "Now",
    title: "Audience language alignment",
    body:
      "Roadmap audience (communities/nonprofits/cities/foundations) doesn't match \"shareholders\" + DBE minority-led focus. Either broaden site copy or tighten the roadmap to the DBE-led small-business niche we actually serve.",
  },
  {
    phase: "Next (60–120 days)",
    title: "Add the missing Build layers to Projects",
    body:
      "Today's Project ≈ Problem + Actions + Score. Add explicit Asset, Stakeholder, Resource, Impact, Insight fields. Start with optional sections; promote to required as adoption grows.",
  },
  {
    phase: "Next",
    title: "Ship Revision Layer (versioning)",
    body:
      "Append-only revisions on ideas/projects with a visible \"v1.0, v1.1\" history and diff view. Without this, the \"living system of knowledge\" claim is aspirational.",
  },
  {
    phase: "Next",
    title: "Stand up Services intake",
    body:
      "Lightweight Services page (Funding Acceleration / Build Design / Intelligence) + intake form → admin queue. This is the early revenue engine in the doc and we have nothing live for it.",
  },
  {
    phase: "Next",
    title: "Add missing focus areas",
    body:
      "Add Neighborhood Revitalization, Housing, Food Systems to the 5 current pillars — or justify why we cut them. Update mem://project/focus-areas either way.",
  },
  {
    phase: "Later (120+ days)",
    title: "Tiered memberships → live billing",
    body:
      "Wire Free / Individual / Org tiers to actual Stripe products. Gate Build creation, private workspaces, team collab, portfolio dashboards by tier per the doc.",
  },
  {
    phase: "Later",
    title: "Structured Insight Layer",
    body:
      "Promote validated patterns from comments/articles into typed Insight objects attached to Builds. Show \"validated across N contexts\" signal — this is the compounding intelligence claim.",
  },
  {
    phase: "Later",
    title: "Impact dashboards",
    body:
      "Longitudinal outcome tracking per Build (jobs, $, foot traffic, occupancy) feeding portfolio reporting for Org-tier customers.",
  },
];

const PHASE_GROUPS = ["Now (next 30–60 days)", "Next (60–120 days)", "Later (120+ days)"];

const RoadmapTab = () => {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl font-bold mb-1">Founder Roadmap Alignment</h2>
        <p className="text-sm text-muted-foreground">
          How the live product maps to <span className="font-mono">EMRKT Founder Roadmap v0.1.1</span> — contradictions and pivots to plan against.
        </p>
      </div>

      <div className="bg-card border border-border rounded-[0.875rem] p-5">
        <h3 className="font-display font-semibold text-sm mb-4">Alignment matrix</h3>
        <div className="space-y-3">
          {ALIGNMENT.map((row) => {
            const meta = STATUS_META[row.status];
            const Icon = meta.Icon;
            return (
              <div
                key={row.area}
                className="border border-border rounded-[0.625rem] p-4 hover:bg-muted/20 transition-colors"
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <h4 className="font-display font-semibold text-sm">{row.area}</h4>
                  <span
                    className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full shrink-0 inline-flex items-center gap-1 ${meta.cls}`}
                  >
                    <Icon className="h-3 w-3" />
                    {meta.label}
                  </span>
                </div>
                <div className="grid md:grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="text-muted-foreground uppercase tracking-wide text-[10px] mb-1">Today</div>
                    <div className="text-foreground">{row.today}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground uppercase tracking-wide text-[10px] mb-1">Roadmap</div>
                    <div className="text-foreground">{row.roadmap}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="font-display font-semibold text-sm">Pivots to plan against</h3>
        {PHASE_GROUPS.map((phase) => {
          const items = PIVOTS.filter((p) => p.phase === phase);
          if (items.length === 0) return null;
          return (
            <div
              key={phase}
              className="bg-card border border-border rounded-[0.875rem] p-5"
            >
              <div className="text-xs uppercase tracking-wide text-primary mb-3">{phase}</div>
              <ul className="space-y-3">
                {items.map((p) => (
                  <li key={p.title} className="flex gap-3">
                    <ArrowRight className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    <div>
                      <div className="font-display font-semibold text-sm">{p.title}</div>
                      <p className="text-xs text-muted-foreground mt-0.5">{p.body}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RoadmapTab;